// @flow
import React from 'react';
import useIsAdmin from '@streaming-projects/useIsAdmin';
import {
  Number4DPRenderer,
  StringArrayToCommaSeparatedStringRenderer,
  NumberAndNaNComparator,
  Number6DPRenderer,
} from '@src/components/views/grid-utils/renderers';
import { PaddedAndRaisedSegment } from '@presentational/PaddedAndRaisedSegment';
import { DataGrid } from '@components/views/grid-utils/DataGrid';
import { getValueOrNaNFromDataObject } from '@streaming-projects/utils';

import { useShouldPricingBeDisabled } from '../useShouldPricingBeEnabled';

export const SPUserCommitCustomViewsContainer = ({ data }) => {
  const inputDataSource = data?.commit_custom_views ?? [];

  const isUserAdmin = useIsAdmin();

  const columnDefs = [
    {
      field: 'Account Name',
      backEndFieldName: 'account_name',
    },
    {
      field: 'Account ID',
      backEndFieldName: 'account_id',
    },
    {
      field: 'Org Name',
      backEndFieldName: 'org_name',
    },
    {
      field: 'Org ID',
      backEndFieldName: 'org_id',
    },
    {
      field: 'Streaming Project Name',
      backEndFieldName: 'sp_name',
    },
    {
      field: 'Streaming Project ID',
      backEndFieldName: 'sp_id',
    },
    {
      field: 'Workload Names',
      backEndFieldName: 'workload_name',
      cellRenderer: StringArrayToCommaSeparatedStringRenderer,
      width: 300,
    },
    {
      field: 'Workload IDs',
      headerName: 'Workload IDs',
      backEndFieldName: 'workload_id',
      cellRenderer: StringArrayToCommaSeparatedStringRenderer,
      width: 300,
    },
    {
      field: 'Resource ID',
      backEndFieldName: 'resource_id',
    },
    {
      field: 'Cluster Name',
      backEndFieldName: 'cluster_name',
    },
    {
      field: 'Cluster Type',
      backEndFieldName: 'cluster_type',
    },
    {
      field: 'Availability Requirement',
      backEndFieldName: 'availability',
    },
    {
      field: 'Provider',
      backEndFieldName: 'provider',
    },
    {
      field: 'Region',
      backEndFieldName: 'region',
    },
    {
      field: 'Networking Type',
      backEndFieldName: 'networking_type',
    },
    {
      field: 'Product',
      backEndFieldName: 'product',
    },
    {
      field: 'Product Sub Category',
      backEndFieldName: 'product_sub_category',
    },
    {
      field: 'Year',
      backEndFieldName: 'year',
    },
    {
      field: 'Month',
      backEndFieldName: 'month',
      sortable: false,
    },
    {
      field: 'Absolute Month Index',
      backEndFieldName: 'absolute_month_index',
      sort: 'asc',
    },
    {
      field: 'Metric',
      backEndFieldName: 'metric',
    },
    {
      field: 'Unit Price',
      backEndFieldName: 'unit_price',
      filter: false,
      cellRenderer: Number6DPRenderer,
    },
    {
      field: 'Unit Price (Discounted)',
      backEndFieldName: 'unit_price_discounted',
      filter: false,
      cellRenderer: Number6DPRenderer,
    },
    {
      field: 'Total Commit (List)',
      backEndFieldName: 'total_commit',
      filter: false,
      cellRenderer: Number4DPRenderer,
    },
    {
      field: 'Total Commit (Discounted)',
      backEndFieldName: 'total_commit_discounted',
      filter: false,
      cellRenderer: Number4DPRenderer,
    },
    {
      field: 'Total Invoiced',
      backEndFieldName: 'total_invoiced',
      filter: false,
      cellRenderer: Number4DPRenderer,
    },
  ];

  if (isUserAdmin) {
    columnDefs.push({
      field: 'Unit Cost',
      backEndFieldName: 'unit_cost',
      filter: false,
      cellRenderer: Number6DPRenderer,
    });
    columnDefs.push({
      field: 'Cost',
      backEndFieldName: 'cost',
      filter: false,
      cellRenderer: Number4DPRenderer,
    });
    columnDefs.push({
      field: 'Margin',
      backEndFieldName: 'margin',
      filter: false,
      comparator: NumberAndNaNComparator,
      valueGetter: (params) => getValueOrNaNFromDataObject(params?.data?.Margin),
    });
    columnDefs.push({
      field: 'Quantity',
      backEndFieldName: 'quantity',
      filter: false,
      comparator: NumberAndNaNComparator,
      valueGetter: (params) => getValueOrNaNFromDataObject(params?.data?.Quantity),
    });
    columnDefs.push({
      field: 'Daily Quantity',
      backEndFieldName: 'daily_quantity',
      filter: false,
      comparator: NumberAndNaNComparator,
      valueGetter: (params) => getValueOrNaNFromDataObject(params?.data?.['Daily Quantity']),
    });
  }

  const shouldPricingBeDisabled = useShouldPricingBeDisabled();

  return (
    <PaddedAndRaisedSegment disabled={shouldPricingBeDisabled}>
      <DataGrid
        columnDefs={columnDefs}
        customOnFirstDataRenderedFunc={() => {}}
        height="400px"
        inputDataSource={inputDataSource}
        label="Commit Custom Views"
      />
    </PaddedAndRaisedSegment>
  );
};
